<template>
  <div class="sls">
    <div class="sls-page-head">
      <div class="sls-page-head-text">
        <h1 class="sls-title">Hijacker</h1>
        <p class="sls-subtitle">Name competitors and choose how SentryKit treats them on each marketplace.</p>
        <p class="sls-page-note">Storefront and marketplace change which sellers you see below. A seller's name and role are saved per marketplace and apply across all your storefronts.</p>
      </div>
      <div v-if="!catalogEmpty" class="sls-head-actions">
        <button class="sls-btn sls-btn-outline sls-btn-compact" type="button" @click="openExport">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></svg>
          Export
        </button>
        <div class="sls-status-line">
          Last updated <strong>{{ refreshedLabel }}</strong> · refreshes automatically every day
          <button
            class="sls-refresh-btn"
            :class="{ 'sls-refresh-spin': backfilling }"
            type="button"
            title="Re-scan your listings now"
            aria-label="Refresh now"
            :disabled="backfilling"
            @click="backfill"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
          </button>
        </div>
      </div>
    </div>

    <div class="sls-filter-bar">
      <div class="sls-filters">
        <select
          class="sls-select sls-filter-select"
          aria-label="Storefront"
          v-model="filterSellerId"
        >
          <option value="">All storefronts</option>
          <option v-for="acc in storefrontOptions" :key="'sf-' + acc.id" :value="String(acc.id)">{{ acc.name }}</option>
        </select>
        <select
          class="sls-select sls-filter-select"
          aria-label="Marketplace"
          v-model="filterMarketplace"
        >
          <option value="">All marketplaces</option>
          <option v-for="code in marketplaceOptions" :key="'mp-' + code" :value="code">{{ code }}</option>
        </select>
      </div>
    </div>

    <div v-if="loading && !rows.length" class="sls-loading"><div class="sls-spinner"></div></div>

    <div v-if="loadError && !rows.length && !loading" class="sls-error">
      <p class="sls-error-title">Could not load sellers</p>
      <p class="sls-error-msg">{{ loadError }}</p>
      <button class="sls-btn sls-btn-outline" type="button" @click="fetchList">Retry</button>
    </div>

    <template v-if="rows.length || (!loading && !loadError)">
      <div v-if="catalogEmpty" class="sls-empty">
        <svg class="sls-empty-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        <div>
          <p class="sls-empty-title">See who's competing for your Buy Box</p>
          <p class="sls-empty-copy">Backfilling scans your ASINs for every seller who currently has a competing offer on them. Name them once and every future alert uses their name instead of a random-looking ID.</p>
          <button
            class="sls-btn sls-btn-primary"
            type="button"
            :disabled="backfilling"
            :class="{ 'sls-btn-loading': backfilling }"
            @click="backfill"
          >{{ backfilling ? 'Scanning your listings…' : 'Backfill from your listings' }}</button>
        </div>
      </div>

      <template v-else>
        <div v-if="totalItems" class="sls-progress">
          <span><strong>{{ classifiedCount }}</strong> of {{ totalItems }} sellers classified</span>
          <div class="sls-progress-bar" aria-hidden="true"><span :style="{ width: classifiedPercent + '%' }"></span></div>
        </div>

        <div v-if="selectedIds.length" class="sls-bulkbar">
          <span><strong>{{ selectedIds.length }}</strong> selected</span>
          <div class="sls-bulkbar-actions">
            <select v-model="bulkRole" class="sls-select sls-bulk-select" aria-label="Role for selected sellers">
              <option v-for="opt in assignableRoles" :key="'bulk-' + opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
            <button
              class="sls-btn sls-btn-outline sls-btn-compact"
              type="button"
              :disabled="bulkSaving"
              :class="{ 'sls-btn-loading': bulkSaving }"
              @click="applyBulkRole"
            >Apply role</button>
            <button class="sls-btn sls-btn-outline sls-btn-compact" type="button" @click="exportSelected">Export selected</button>
          </div>
        </div>

        <div class="sls-table-wrap" :class="{ 'sls-table-busy': loading, 'sls-table-wrap--bulk': selectedIds.length }">
          <table class="sls-table">
            <thead>
              <tr>
                <th class="sls-th-check">
                  <input
                    type="checkbox"
                    aria-label="Select all on this page"
                    :checked="allSelectableChecked"
                    :disabled="!selectableRows.length"
                    @change="toggleSelectAll($event.target.checked)"
                  >
                </th>
                <th>Marketplace</th>
                <th>Seller</th>
                <th>Status</th>
                <th>Name</th>
                <th>Role <span class="sls-info" tabindex="0" :title="roleTooltip" :aria-label="roleTooltip">ⓘ</span></th>
                <th class="sls-th-sorted" title="Sorted by ASIN count, highest first">ASINs ▼</th>
                <th>First seen <span class="sls-info" tabindex="0" title="When SentryKit first saw this seller's current offer on one of your ASINs.">ⓘ</span></th>
                <th>Last seen on Buy Box <span class="sls-info" tabindex="0" title="The most recent time this seller's offer on one of your ASINs changed or was confirmed.">ⓘ</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!rows.length && !loading">
                <td colspan="9" class="sls-td-empty">No competing offers for this storefront / marketplace.</td>
              </tr>
              <tr
                v-for="row in rows"
                :key="row.id"
                :id="'seller-row-' + row.id"
                :class="{ 'sls-row-highlight': highlightId === row.id, 'sls-row-suggested': hasSuggestion(row) }"
              >
                <td class="sls-td-check">
                  <input
                    v-if="!isSelf(row)"
                    type="checkbox"
                    :value="row.id"
                    :checked="isSelected(row.id)"
                    @change="toggleSelected(row.id, $event.target.checked)"
                  >
                </td>
                <td>
                  <span class="sls-mp-badge">{{ marketplaceCode(row) }}</span>
                </td>
                <td class="sls-td-seller">
                  <a
                    v-if="profileUrl(row)"
                    class="sls-seller-name"
                    :href="profileUrl(row)"
                    target="_blank"
                    rel="noopener noreferrer"
                  >{{ displayName(row) }}</a>
                  <span v-else class="sls-seller-name">{{ displayName(row) }}</span>
                  <div class="sls-token">{{ merchantToken(row) }}</div>
                </td>
                <td>
                  <span v-if="isSelf(row)" class="sls-pill sls-pill-informational">You</span>
                  <span
                    v-else-if="pillLabel(row)"
                    class="sls-pill"
                    :class="'sls-pill-' + pillTone(row)"
                  >{{ pillLabel(row) }}</span>
                  <span v-else class="sls-pill sls-pill-neutral">New</span>
                </td>
                <td class="sls-td-name">
                  <input
                    class="sls-name-input"
                    type="text"
                    :value="labelDraft(row)"
                    :placeholder="namePlaceholder(row)"
                    :disabled="savingIds[row.id]"
                    @input="onLabelInput(row, $event.target.value)"
                    @keydown.enter.prevent="saveLabel(row)"
                    @blur="saveLabel(row)"
                  >
                  <button
                    v-if="hasSuggestion(row)"
                    type="button"
                    class="sls-suggest"
                    :disabled="savingIds[row.id]"
                    @click="acceptSuggestion(row)"
                  >Use “{{ row.suggested_label }}”{{ row.suggested_role ? ' as ' + roleLabel(row.suggested_role) : '' }}</button>
                  <p v-else-if="showNameHint(row)" class="sls-field-hint">Name this seller so the next alert uses it.</p>
                </td>
                <td class="sls-td-role">
                  <template v-if="isSelf(row)">
                    <span class="sls-you">Your account</span>
                  </template>
                  <template v-else>
                    <select
                      class="sls-select"
                      :value="roleValue(row)"
                      :title="roleHelper(row)"
                      :disabled="!!savingIds[row.id]"
                      @change="saveRole(row, $event.target.value)"
                    >
                      <option v-for="opt in assignableRoles" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                    </select>
                    <p v-if="showDigestNote(row)" class="sls-field-hint">Instant Buy Box emails until Offer activity digest is on.</p>
                  </template>
                </td>
                <td class="sls-td-asins">{{ asinCount(row) }}</td>
                <td class="sls-td-seen">{{ formatDay(row.first_seen) }}</td>
                <td class="sls-td-seen">{{ formatDay(row.last_seen) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="sls-pagination">
          <span class="sls-page-label">{{ pageLabel }}</span>
          <div class="sls-page-controls">
            <button class="sls-page-btn" type="button" :disabled="page <= 1 || loading" @click="goToPage(1)">«</button>
            <button class="sls-page-btn" type="button" :disabled="page <= 1 || loading" @click="goToPage(page - 1)">‹</button>
            <span class="sls-page-num">{{ page }} / {{ totalPages }}</span>
            <button class="sls-page-btn" type="button" :disabled="page >= totalPages || loading" @click="goToPage(page + 1)">›</button>
            <button class="sls-page-btn" type="button" :disabled="page >= totalPages || loading" @click="goToPage(totalPages)">»</button>
          </div>
        </div>
      </template>
    </template>

    <div v-if="exportOpen" class="sls-modal-backdrop" @click.self="exportOpen = false">
      <div class="sls-modal" role="dialog" aria-modal="true" aria-labelledby="sls-export-title">
        <h3 id="sls-export-title" class="sls-modal-title">Export sellers</h3>
        <p class="sls-modal-hint">Only sellers with offer activity inside this window are included. Storefront and marketplace filters apply.</p>
        <div class="sls-modal-dates">
          <label>From<input v-model="exportFrom" type="date" :max="exportTo"></label>
          <label>To<input v-model="exportTo" type="date" :min="exportFrom"></label>
        </div>
        <div class="sls-modal-formats">
          <label><input v-model="exportFormat" type="radio" value="csv"> CSV</label>
          <label><input v-model="exportFormat" type="radio" value="xlsx"> XLSX</label>
        </div>
        <p v-if="exportRangeError" class="sls-modal-error">{{ exportRangeError }}</p>
        <div class="sls-modal-actions">
          <button class="sls-btn sls-btn-outline sls-btn-compact" type="button" @click="exportOpen = false">Cancel</button>
          <button
            class="sls-btn sls-btn-primary sls-btn-compact"
            type="button"
            :disabled="exporting || !!exportRangeError"
            :class="{ 'sls-btn-loading': exporting }"
            @click="exportReport"
          >{{ exporting ? 'Exporting…' : 'Export' }}</button>
        </div>
      </div>
    </div>

    <transition name="sls-slide-up">
      <div v-if="successMsg" class="sls-toast sls-toast-ok">{{ successMsg }}</div>
    </transition>
    <transition name="sls-slide-up">
      <div v-if="errorMsg" class="sls-toast sls-toast-err">{{ errorMsg }}</div>
    </transition>
  </div>
</template>

<script>
import {
  extractV1SellersItems,
  mapV1SellersToFilterAccounts,
  extractMarketplaceCodesFromV1SellersBody
} from '../../utils/sellerAccountsFromV1Api.js'
import {
  accountSellersReportUrl,
  filenameFromContentDisposition,
  csvHasDataRows,
  triggerBlobDownload,
  sellersCsv
} from '../../utils/accountSellersReport.js'

/** YYYY-MM-DD in the browser's local calendar (for <input type="date">). */
function isoDay (d) {
  var m = String(d.getMonth() + 1)
  var day = String(d.getDate())
  return d.getFullYear() + '-' + (m.length < 2 ? '0' + m : m) + '-' + (day.length < 2 ? '0' + day : day)
}

/**
 * Matches Alerts::Routing::DEFAULTS. "Digest" there is the offer activity digest (every 15 minutes),
 * and it only batches while that setting is on — otherwise those alerts still send immediately.
 */
var ROLE_TOOLTIP = 'Authorised reseller and Ignore send no emails for that seller on that marketplace: no new-seller (hijacker), ' +
  'Buy Box lost, competitor price or No Featured Offer emails (No Featured Offer: when they held the Buy Box just before). ' +
  'Amazon Retail never triggers new-seller or No Featured Offer emails; its Buy Box wins are batched into the offer activity digest ' +
  '(every 15 minutes) when it is on in Alert Settings, and email immediately while it is off. Watchlist always emails.'

var ASSIGNABLE_ROLES = [
  { value: 'UNKNOWN', label: 'Unclassified' },
  { value: 'AMAZON', label: 'Amazon Retail' },
  { value: 'AUTHORISED', label: 'Authorised reseller' },
  { value: 'WATCH', label: 'Watchlist' },
  { value: 'IGNORE', label: 'Ignore' }
]

var ROLE_LABELS = {
  UNKNOWN: 'Unclassified',
  AMAZON: 'Amazon Retail',
  AUTHORISED: 'Authorised',
  WATCH: 'Watchlist',
  IGNORE: 'Ignore'
}

function apiMessage (err) {
  var msg = err && err.body && err.body.message
  if (Array.isArray(msg)) return msg.filter(Boolean).join(', ')
  if (typeof msg === 'string' && msg) return msg
  return (err && err.statusText) || 'Something went wrong'
}

function parseBool (v) {
  return v === true || v === 1 || v === '1' || v === 'true'
}

function roleOf (row) {
  var chip = row && row.chip
  var raw = (chip && chip.role) || (row && row.role) || ''
  return String(raw).toUpperCase()
}

export default {
  name: 'SellersSettings',
  data: function () {
    return {
      loading: false,
      loadError: null,
      rows: [],
      totalItems: 0,
      page: 1,
      pageSize: 50,
      selectedIds: [],
      bulkRole: 'AUTHORISED',
      bulkSaving: false,
      backfilling: false,
      savingIds: {},
      labelDrafts: {},
      highlightId: null,
      offerActivityDigest: false,
      successMsg: '',
      errorMsg: '',
      toastTimer: null,
      exporting: false,
      storefronts: [],
      marketplaceCodes: [],
      filterSellerId: '',
      filterMarketplace: '',
      classifiedCount: 0,
      refreshedAt: null,
      exportOpen: false,
      exportFrom: '',
      exportTo: '',
      exportFormat: 'csv'
    }
  },
  computed: {
    assignableRoles: function () {
      return ASSIGNABLE_ROLES
    },
    roleTooltip: function () {
      return ROLE_TOOLTIP
    },
    classifiedPercent: function () {
      if (!this.totalItems) return 0
      return Math.min(100, Math.round((this.classifiedCount / this.totalItems) * 100))
    },
    refreshedLabel: function () {
      if (!this.refreshedAt) return 'not yet'
      var d = new Date(this.refreshedAt)
      if (isNaN(d.getTime())) return 'not yet'
      var time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      var today = new Date()
      var yesterday = new Date(today.getTime() - 86400000)
      if (d.toDateString() === today.toDateString()) return 'today at ' + time
      if (d.toDateString() === yesterday.toDateString()) return 'yesterday at ' + time
      return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) + ' at ' + time
    },
    exportRangeError: function () {
      if (!this.exportFrom || !this.exportTo) return 'Pick both dates.'
      if (this.exportFrom > this.exportTo) return '“From” must be on or before “To”.'
      return ''
    },
    hasActiveFilters: function () {
      return !!(this.filterSellerId || this.filterMarketplace)
    },
    catalogEmpty: function () {
      return this.totalItems === 0 && !this.loading && !this.hasActiveFilters
    },
    listFilterKey: function () {
      return this.filterSellerId + '|' + this.filterMarketplace + '|' + this.queryToken()
    },
    storefrontOptions: function () {
      var list = this.storefronts.slice()
      var current = this.filterSellerId
      if (!current) return list
      var found = list.some(function (s) { return String(s.id) === String(current) })
      if (!found) list.push({ id: current, name: 'Storefront ' + current })
      return list
    },
    marketplaceOptions: function () {
      var list = this.marketplaceCodes.slice()
      var current = this.filterMarketplace
      if (current && list.indexOf(current) < 0) list = list.concat([current])
      return list
    },
    selectableRows: function () {
      return this.rows.filter(function (row) { return roleOf(row) !== 'SELF' })
    },
    allSelectableChecked: function () {
      if (!this.selectableRows.length) return false
      var selected = this.selectedIds
      return this.selectableRows.every(function (row) {
        return selected.indexOf(row.id) > -1
      })
    },
    totalPages: function () {
      return Math.max(1, Math.ceil(this.totalItems / this.pageSize) || 1)
    },
    pageLabel: function () {
      if (!this.totalItems) return '0 sellers'
      var start = (this.page - 1) * this.pageSize + 1
      var end = Math.min(this.page * this.pageSize, this.totalItems)
      return start + '–' + end + ' of ' + this.totalItems
    }
  },
  watch: {
    listFilterKey: function () {
      this.page = 1
      this.fetchList()
    }
  },
  mounted: function () {
    this.clearFilterQuery()
    this.fetchList()
    this.loadDigestFlag()
    this.loadStorefronts()
  },
  beforeDestroy: function () {
    if (this.toastTimer) clearTimeout(this.toastTimer)
  },
  methods: {
    queryToken: function () {
      var q = this.$route.query || {}
      var t = q.token
      if (Array.isArray(t)) t = t[0]
      return t ? String(t) : ''
    },
    queryMarketplace: function () {
      var m = this.filterMarketplace
      if (!m) return ''
      m = String(m).toUpperCase()
      if (m === 'GB') m = 'UK'
      return m
    },
    querySellerId: function () {
      var s = this.filterSellerId
      return s != null && s !== '' ? String(s) : ''
    },
    clearFilterQuery: function () {
      var q = Object.assign({}, this.$route.query || {})
      if (q.seller_id == null && q.sellerId == null && q.marketplace == null) return
      delete q.seller_id
      delete q.sellerId
      delete q.marketplace
      this.$router.replace({ query: q })
    },
    loadStorefronts: function () {
      var self = this
      this.$http.get('/api/v1/sellers').then(function (res) {
        var body = (res && res.body) || {}
        self.storefronts = mapV1SellersToFilterAccounts(extractV1SellersItems(body))
        self.marketplaceCodes = extractMarketplaceCodesFromV1SellersBody(body)
      }, function () {
        if (!self.storefronts.length) self.storefronts = []
        if (!self.marketplaceCodes.length) self.marketplaceCodes = []
      })
    },
    fetchList: function () {
      var self = this
      this.loading = true
      this.loadError = null
      var params = {
        page: this.page,
        pageSize: this.pageSize
      }
      var token = this.queryToken()
      var marketplace = this.queryMarketplace()
      var sellerId = this.querySellerId()
      if (token) params.token = token
      if (marketplace) params.marketplace = marketplace
      if (sellerId) params.seller_id = sellerId
      return this.$http.get('/api/v1/account-sellers', { params: params }).then(function (res) {
        var body = res.body || {}
        var list = body.account_sellers || []
        self.rows = list
        var total = body.totalItems
        if (total == null && body.meta) total = body.meta.total
        self.totalItems = total != null ? Number(total) : list.length
        var pg = body.currentPage
        if (pg == null && body.meta) pg = body.meta.page
        if (pg != null && Number(pg) > 0) self.page = Number(pg)
        var ps = body.pageSize
        if (ps == null && body.meta) ps = body.meta.per_page
        if (ps != null && Number(ps) > 0) self.pageSize = Number(ps)
        self.classifiedCount = Number(body.classifiedCount) || 0
        self.refreshedAt = body.refreshedAt || null
        var drafts = {}
        list.forEach(function (row) {
          drafts[row.id] = row.label == null ? '' : String(row.label)
        })
        self.labelDrafts = drafts
        self.selectedIds = []
        self.loading = false
        self.afterListLoaded()
      }, function (err) {
        self.loading = false
        if (err && err.status === 401) return
        var msg = apiMessage(err)
        self.loadError = msg
        if (self.rows.length) self.toastError(msg)
      })
    },
    afterListLoaded: function () {
      var self = this
      var open = this.rows.filter(function (row) { return row.editor_open === true })[0]
      var token = this.queryToken()
      var marketplace = this.queryMarketplace()
      if (!open && token) {
        open = this.rows.filter(function (row) {
          var sameToken = self.merchantToken(row) === token
          if (!sameToken) return false
          if (!marketplace) return true
          return self.marketplaceCode(row) === marketplace
        })[0]
      }
      this.highlightId = open ? open.id : null
      if (!open) return
      this.$nextTick(function () {
        var el = document.getElementById('seller-row-' + open.id)
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center' })
        var chip = open.chip || {}
        if (chip.name_this_seller === true) {
          var input = el && el.querySelector('.sls-name-input')
          if (input) {
            input.focus()
            if (input.select) input.select()
          }
        }
      })
    },
    loadDigestFlag: function () {
      var self = this
      this.$http.get('/api/identity/settings').then(function (res) {
        var body = res.body || {}
        var v = body.offerActivityDigest
        if (v == null) v = body.offer_activity_digest
        self.offerActivityDigest = parseBool(v)
      }, function () {})
    },
    goToPage: function (p) {
      var next = Math.min(this.totalPages, Math.max(1, p))
      if (next === this.page) return
      this.page = next
      this.fetchList()
    },
    backfill: function () {
      var self = this
      this.backfilling = true
      var firstScan = this.catalogEmpty
      this.$http.post('/api/v1/account-sellers/backfill').then(function (res) {
        self.backfilling = false
        var body = res.body || {}
        var added = body.added != null ? Number(body.added) : Number(body.upserted || 0)
        if (body.refreshedAt) self.refreshedAt = body.refreshedAt
        if (firstScan) {
          self.toastOk(added ? 'Found ' + added + ' seller' + (added === 1 ? '' : 's') + '.' : 'No competing sellers found yet.')
        } else {
          self.toastOk(added ? 'Refreshed — ' + added + ' new seller' + (added === 1 ? '' : 's') + ' found' : 'Refreshed — no new sellers found')
        }
        self.page = 1
        self.fetchList()
      }, function (err) {
        self.backfilling = false
        if (err && err.status === 401) return
        self.toastError(apiMessage(err))
      })
    },
    isSelf: function (row) {
      return roleOf(row) === 'SELF'
    },
    merchantToken: function (row) {
      var chip = row && row.chip
      return (chip && chip.token) || (row && row.merchant_token) || ''
    },
    marketplaceCode: function (row) {
      return String((row && row.marketplace_code) || '').toUpperCase()
    },
    displayName: function (row) {
      var chip = row && row.chip
      if (chip && chip.display_name) return chip.display_name
      return this.merchantToken(row)
    },
    profileUrl: function (row) {
      var chip = row && row.chip
      var url = chip && chip.profile_url
      return typeof url === 'string' && url ? url : null
    },
    pillLabel: function (row) {
      var chip = row && row.chip
      return chip && chip.pill ? chip.pill : ''
    },
    pillTone: function (row) {
      var chip = row && row.chip
      var tone = chip && chip.tone
      if (tone === 'positive' || tone === 'informational' || tone === 'alarm' || tone === 'caution' || tone === 'neutral') {
        return tone
      }
      return 'neutral'
    },
    asinCount: function (row) {
      var n = row && row.asin_count
      if (n == null || n === '') return 0
      var num = Number(n)
      return isNaN(num) ? 0 : num
    },
    roleValue: function (row) {
      var r = roleOf(row)
      if (r === 'SELF') return r
      for (var i = 0; i < ASSIGNABLE_ROLES.length; i++) {
        if (ASSIGNABLE_ROLES[i].value === r) return r
      }
      return 'UNKNOWN'
    },
    labelDraft: function (row) {
      if (Object.prototype.hasOwnProperty.call(this.labelDrafts, row.id)) {
        return this.labelDrafts[row.id]
      }
      return row.label == null ? '' : String(row.label)
    },
    onLabelInput: function (row, value) {
      this.$set(this.labelDrafts, row.id, value)
    },
    namePlaceholder: function (row) {
      return row && row.suggested_label ? row.suggested_label + '?' : 'Name this seller…'
    },
    showNameHint: function (row) {
      var chip = row && row.chip
      return !!(chip && chip.name_this_seller)
    },
    showDigestNote: function (row) {
      if (this.offerActivityDigest) return false
      var r = roleOf(row)
      return r === 'AMAZON'
    },
    roleHelper: function (row) {
      var r = this.roleValue(row)
      if (r === 'IGNORE') return 'No emails for this id on this marketplace (Buy Box, new seller, competitor price, No Featured Offer).'
      if (r === 'AUTHORISED') return 'Known reseller, not a hijacker. No emails for this id on this marketplace; Buy Box moves still show in Issues.'
      if (r === 'AMAZON') return 'Shown as Amazon Retail. No new-seller or No Featured Offer alerts; Buy Box wins go to the offer activity digest when it is on.'
      if (r === 'WATCH') return 'Alarm. Emails send. Copy may say “hijacker”.'
      return 'Not classified yet. New-seller and Buy Box alerts send as normal.'
    },
    roleLabel: function (role) {
      return ROLE_LABELS[String(role || '').toUpperCase()] || role
    },
    /** "12 Aug 2026", or Today / Yesterday. */
    formatDay: function (iso) {
      if (!iso) return '—'
      var d = new Date(iso)
      if (isNaN(d.getTime())) return '—'
      var today = new Date()
      if (d.toDateString() === today.toDateString()) return 'Today'
      if (d.toDateString() === new Date(today.getTime() - 86400000).toDateString()) return 'Yesterday'
      return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })
    },
    hasSuggestion: function (row) {
      if (!row || this.isSelf(row) || !row.suggested_label) return false
      return String(this.labelDraft(row) || '').trim() === '' && !row.label
    },
    /** Saves the suggested name (and role, for Amazon Retail) — the seller can still overwrite either. */
    acceptSuggestion: function (row) {
      var self = this
      var body = { label: row.suggested_label }
      if (row.suggested_role && roleOf(row) === 'UNKNOWN') body.role = row.suggested_role
      this.$set(this.savingIds, row.id, true)
      this.$http.patch('/api/v1/account-sellers/' + encodeURIComponent(String(row.id)), body).then(function (res) {
        self.$set(self.savingIds, row.id, false)
        var updated = (res.body && res.body.account_seller) || res.body
        if (updated) self.applyRow(updated)
        self.classifiedCount += body.role ? 1 : 0
      }, function (err) {
        self.$set(self.savingIds, row.id, false)
        if (err && err.status === 401) return
        self.toastError(apiMessage(err))
      })
    },
    isSelected: function (id) {
      return this.selectedIds.indexOf(id) > -1
    },
    toggleSelected: function (id, on) {
      var next = this.selectedIds.slice()
      var i = next.indexOf(id)
      if (on && i < 0) next.push(id)
      if (!on && i > -1) next.splice(i, 1)
      this.selectedIds = next
    },
    toggleSelectAll: function (on) {
      if (!on) {
        this.selectedIds = []
        return
      }
      this.selectedIds = this.selectableRows.map(function (row) { return row.id })
    },
    applyRow: function (updated) {
      if (!updated || updated.id == null) return
      var i = -1
      for (var n = 0; n < this.rows.length; n++) {
        if (this.rows[n].id === updated.id) { i = n; break }
      }
      if (i > -1) this.$set(this.rows, i, updated)
      this.$set(this.labelDrafts, updated.id, updated.label == null ? '' : String(updated.label))
    },
    saveLabel: function (row) {
      if (this.isSelf(row) || this.savingIds[row.id]) return
      var next = String(this.labelDraft(row) || '').trim()
      var prev = row.label == null ? '' : String(row.label)
      if (next === prev) return
      var self = this
      this.$set(this.savingIds, row.id, true)
      this.$http.patch('/api/v1/account-sellers/' + encodeURIComponent(String(row.id)), {
        label: next
      }).then(function (res) {
        self.$set(self.savingIds, row.id, false)
        var updated = (res.body && res.body.account_seller) || res.body
        if (updated) self.applyRow(updated)
      }, function (err) {
        self.$set(self.savingIds, row.id, false)
        self.$set(self.labelDrafts, row.id, prev)
        if (err && err.status === 401) return
        self.toastError(apiMessage(err))
      })
    },
    saveRole: function (row, role) {
      if (this.isSelf(row)) return
      var next = String(role || '').toUpperCase()
      if (next === 'SELF') return
      if (next === roleOf(row)) return
      var self = this
      this.$set(this.savingIds, row.id, true)
      this.$http.patch('/api/v1/account-sellers/' + encodeURIComponent(String(row.id)), {
        role: next
      }).then(function (res) {
        self.$set(self.savingIds, row.id, false)
        var updated = (res.body && res.body.account_seller) || res.body
        if (updated) self.applyRow(updated)
      }, function (err) {
        self.$set(self.savingIds, row.id, false)
        if (err && err.status === 401) return
        self.toastError(apiMessage(err))
      })
    },
    /** Sets the role on every checked row in one call — no confirm dialog; a toast confirms. */
    applyBulkRole: function () {
      if (!this.selectedIds.length || !this.bulkRole || this.bulkSaving) return
      var role = String(this.bulkRole).toUpperCase()
      if (role === 'SELF') return
      var self = this
      var n = this.selectedIds.length
      this.bulkSaving = true
      this.$http.patch('/api/v1/account-sellers/bulk_update', {
        ids: this.selectedIds.slice(),
        role: role
      }).then(function () {
        self.bulkSaving = false
        self.selectedIds = []
        self.toastOk('Role updated for ' + n + ' seller' + (n === 1 ? '' : 's'))
        self.fetchList()
      }, function (err) {
        self.bulkSaving = false
        if (err && err.status === 401) return
        self.toastError(apiMessage(err))
      })
    },
    /** CSV of just the checked rows, built from what is on screen — downloads immediately. */
    exportSelected: function () {
      var self = this
      var picked = this.rows.filter(function (row) { return self.selectedIds.indexOf(row.id) > -1 })
      if (!picked.length) return
      var cells = picked.map(function (row) {
        return [
          self.merchantToken(row),
          row.label || self.displayName(row),
          self.roleLabel(roleOf(row)),
          self.marketplaceCode(row),
          self.asinCount(row),
          row.first_seen || '',
          row.last_seen || '',
          (row.asins || []).join(', ')
        ]
      })
      var blob = new window.Blob([sellersCsv(cells)], { type: 'text/csv;charset=utf-8' })
      triggerBlobDownload(blob, 'sentrykit-sellers-selected-' + isoDay(new Date()) + '.csv')
      this.toastOk('Exported ' + picked.length + ' selected row' + (picked.length === 1 ? '' : 's') + ' as CSV')
    },
    openExport: function () {
      var to = new Date()
      var from = new Date(to.getTime() - 90 * 86400000)
      if (!this.exportTo) this.exportTo = isoDay(to)
      if (!this.exportFrom) this.exportFrom = isoDay(from)
      this.exportOpen = true
    },
    toastOk: function (msg) {
      this.errorMsg = ''
      this.successMsg = msg
      this.scheduleToastClear()
    },
    toastError: function (msg) {
      this.successMsg = ''
      this.errorMsg = msg
      this.scheduleToastClear()
    },
    scheduleToastClear: function () {
      var self = this
      if (this.toastTimer) clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(function () {
        self.successMsg = ''
        self.errorMsg = ''
      }, 4000)
    },
    exportReport: function () {
      if (this.exporting || this.exportRangeError) return
      var self = this
      var xlsx = this.exportFormat === 'xlsx'
      var url = accountSellersReportUrl({
        sellerId: this.filterSellerId || undefined,
        marketplace: this.filterMarketplace || undefined,
        from: this.exportFrom,
        to: this.exportTo,
        fileFormat: this.exportFormat
      })
      this.exporting = true
      var xhr = new XMLHttpRequest()
      xhr.open('GET', url, true)
      xhr.responseType = 'blob'
      xhr.onload = function () {
        self.exporting = false
        var status = xhr.status
        if (status === 401) return
        if (status < 200 || status >= 300 || !xhr.response) {
          self.toastError('Export failed. Please try again.')
          return
        }
        var blob = xhr.response
        var filename = filenameFromContentDisposition(
          xhr.getResponseHeader('Content-Disposition'),
          'sentrykit-competing-sellers.' + (xlsx ? 'xlsx' : 'csv')
        )
        self.exportOpen = false
        if (xlsx) {
          triggerBlobDownload(blob, filename)
          self.toastOk('Export downloaded.')
          return
        }
        var reader = new FileReader()
        reader.onload = function () {
          if (!csvHasDataRows(reader.result)) {
            self.toastOk('No seller activity in that window for this storefront / marketplace.')
          } else {
            self.toastOk('Export downloaded.')
          }
          triggerBlobDownload(blob, filename)
        }
        reader.onerror = function () {
          triggerBlobDownload(blob, filename)
        }
        reader.readAsText(blob)
      }
      xhr.onerror = function () {
        self.exporting = false
        self.toastError('Export failed. Please try again.')
      }
      xhr.send()
    }
  }
}
</script>

<style scoped>
.sls {
  font-size: 14px;
  color: var(--text-primary);
}

.sls-loading { display: flex; justify-content: center; align-items: center; min-height: 280px; }
.sls-spinner {
  width: 36px; height: 36px;
  border: 3px solid var(--border-light);
  border-top-color: var(--primary-navy);
  border-radius: 50%;
  animation: sls-spin 0.7s linear infinite;
}
@keyframes sls-spin { to { transform: rotate(360deg); } }

.sls-error { text-align: center; padding: 48px 24px; }
.sls-error-title { font-weight: 600; margin: 0 0 8px; }
.sls-error-msg { color: var(--text-secondary); margin-bottom: 16px; }

.sls-page-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}
.sls-page-head-text { flex: 1; min-width: 260px; }
.sls-head-actions { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; }
.sls-status-line {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: var(--text-secondary);
}
.sls-status-line strong { color: var(--text-primary); font-weight: 600; }
.sls-refresh-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid var(--border-medium);
  border-radius: 6px;
  background: var(--bg-white);
  color: var(--primary-navy);
  cursor: pointer;
}
.sls-refresh-btn:hover:not(:disabled) { border-color: var(--primary-navy); }
.sls-refresh-btn:disabled { cursor: progress; }
.sls-refresh-spin svg { animation: sls-spin 0.8s linear infinite; }
.sls-btn-compact {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  font-size: 13px;
  white-space: nowrap;
}
.sls-title { margin: 0 0 6px; font-size: 26px; font-weight: 700; letter-spacing: -0.01em; }
.sls-subtitle { margin: 0; color: var(--text-secondary); line-height: 1.5; max-width: 640px; }
.sls-page-note { margin: 8px 0 0; font-size: 13px; color: var(--text-tertiary); }

.sls-empty {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  max-width: 640px;
  padding: 28px 26px;
  background: var(--bg-white);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-card);
}
.sls-empty-icon { flex-shrink: 0; margin-top: 2px; color: var(--text-tertiary); }
.sls-empty-title { font-size: 15px; font-weight: 600; margin: 0 0 6px; }
.sls-empty-copy { margin: 0 0 14px; font-size: 13px; line-height: 1.55; color: var(--text-secondary); max-width: 480px; }

.sls-progress {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 12px;
  font-size: 12.5px;
  color: var(--text-secondary);
}
.sls-progress strong { color: var(--text-primary); }
.sls-progress-bar { width: 120px; height: 5px; border-radius: 4px; background: var(--bg-subtle); overflow: hidden; }
.sls-progress-bar span { display: block; height: 100%; background: var(--accent-orange, #f5841f); }

.sls-bulkbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 8px 12px;
  font-size: 13px;
  color: #1258b0;
  background: var(--status-info-bg, #eaf2fe);
  border: 1px solid #cfe0fb;
  border-bottom: none;
  border-radius: var(--radius-card) var(--radius-card) 0 0;
}
.sls-bulkbar-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.sls-bulkbar .sls-bulk-select { width: auto; min-width: 180px; height: 34px; padding: 0 10px; }
.sls-table-wrap--bulk { border-radius: 0; }

.sls-info { color: var(--text-tertiary); cursor: help; font-weight: 400; }
.sls-th-sorted { color: var(--primary-navy) !important; }
.sls-row-suggested { background: #fbfdff; }
.sls-suggest {
  display: block;
  margin-top: 6px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--primary-navy);
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  border-bottom: 1px dashed currentColor;
}
.sls-suggest:disabled { opacity: 0.5; cursor: default; }

.sls-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 20, 35, 0.45);
}
.sls-modal {
  width: 100%;
  max-width: 380px;
  padding: 20px 22px;
  background: var(--bg-white);
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
}
.sls-modal-title { margin: 0 0 4px; font-size: 15px; font-weight: 600; }
.sls-modal-hint { margin: 0 0 16px; font-size: 12.5px; color: var(--text-secondary); line-height: 1.45; }
.sls-modal-dates { display: flex; gap: 10px; margin-bottom: 14px; }
.sls-modal-dates label { flex: 1; display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: var(--text-secondary); }
.sls-modal-dates input {
  padding: 6px 8px;
  border: 1px solid var(--border-medium);
  border-radius: 6px;
  font-size: 13px;
  color: var(--text-primary);
}
.sls-modal-formats { display: flex; gap: 16px; margin-bottom: 16px; font-size: 13px; }
.sls-modal-formats label { display: flex; align-items: center; gap: 6px; cursor: pointer; }
.sls-modal-error { margin: -6px 0 12px; font-size: 12px; font-weight: 600; color: #b42318; }
.sls-modal-actions { display: flex; justify-content: flex-end; gap: 8px; }

.sls-filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.sls-filters { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.sls-filters .sls-select {
  width: auto;
  min-width: 160px;
  height: 36px;
  padding: 0 10px;
}
.sls-filter-bar .sls-btn {
  height: 36px;
  padding: 0 14px;
  font-size: 13px;
  line-height: 34px;
  white-space: nowrap;
}

.sls-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.sls-bulk { display: flex; align-items: center; gap: 8px; }
.sls-bulk-select {
  width: auto;
  min-width: 180px;
  height: 36px;
  padding: 0 10px;
}

.sls-table-wrap {
  background: var(--bg-white);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-card) var(--radius-card) 0 0;
  overflow-x: auto;
}
.sls-table-busy { opacity: 0.65; }
.sls-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.sls-table thead th {
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--bg-subtle);
  border-bottom: 1px solid var(--border-light);
  text-align: left;
  white-space: nowrap;
}
.sls-table td {
  padding: 12px;
  vertical-align: top;
  border-bottom: 1px solid var(--border-light);
}
.sls-th-check, .sls-td-check { width: 36px; }
.sls-td-empty {
  text-align: center;
  color: var(--text-secondary);
  padding: 28px 12px;
}
.sls-row-highlight { background: #fff8eb; outline: 2px solid rgba(245, 132, 31, 0.35); outline-offset: -2px; }

.sls-mp-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--bg-subtle);
  border: 1px solid var(--border-light);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
}
.sls-seller-name { font-weight: 600; color: var(--primary-navy); text-decoration: none; }
a.sls-seller-name:hover { text-decoration: underline; }
.sls-token { margin-top: 3px; font-size: 12px; color: var(--text-tertiary); }
.sls-td-asins { font-variant-numeric: tabular-nums; }
.sls-td-seen { white-space: nowrap; color: var(--text-secondary); }
.sls-you { font-weight: 600; color: var(--text-secondary); }

.sls-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  background: var(--bg-subtle);
  color: var(--text-secondary);
}
.sls-pill-positive { background: var(--status-success-bg); color: #166534; }
.sls-pill-informational { background: var(--status-info-bg); color: #1d4ed8; }
.sls-pill-alarm { background: var(--status-critical-bg); color: #b91c1c; }
.sls-pill-caution { background: var(--status-warning-bg); color: #92400e; }
.sls-pill-neutral { background: var(--bg-subtle); color: var(--text-secondary); }

.sls-name-input,
.sls-select {
  width: 100%;
  min-width: 140px;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid var(--border-medium);
  background: var(--bg-white);
  font-size: 13px;
  color: var(--text-primary);
}
.sls-name-input:focus,
.sls-select:focus {
  outline: none;
  border-color: var(--primary-navy);
}
.sls-td-role { min-width: 220px; }
.sls-field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: var(--text-secondary);
}
.sls-field-hint-muted { color: var(--text-tertiary); }

.sls-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--bg-white);
  border: 1px solid var(--border-light);
  border-top: none;
  border-radius: 0 0 var(--radius-card) var(--radius-card);
}
.sls-page-label { font-size: 13px; color: var(--text-secondary); }
.sls-page-controls { display: flex; align-items: center; gap: 4px; }
.sls-page-btn {
  width: 32px; height: 32px;
  border: 1px solid var(--border-light);
  border-radius: 6px;
  background: var(--bg-white);
  color: var(--text-secondary);
  cursor: pointer;
}
.sls-page-btn:hover:not(:disabled) { border-color: var(--primary-navy); color: var(--primary-navy); }
.sls-page-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.sls-page-num { padding: 0 10px; font-size: 13px; font-weight: 500; }

.sls-btn {
  border-radius: var(--radius-btn, 6px);
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
}
.sls-btn-outline { background: white; border: 1px solid var(--border-medium); }
.sls-btn-primary { background: var(--primary-navy); color: #fff; }
.sls-btn-bulk {
  height: 36px;
  padding: 0 14px;
  font-size: 13px;
  white-space: nowrap;
  line-height: 34px;
}
.sls-btn:disabled { cursor: not-allowed; }
.sls-btn-primary:disabled {
  background: #94a3b8;
  color: #fff;
}
.sls-btn-outline:disabled { color: var(--text-tertiary); }
.sls-btn-loading { pointer-events: none; }

.sls-toast {
  position: fixed; bottom: 28px; right: 28px; z-index: 10002;
  padding: 12px 18px; font-size: 14px; border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0,0,0,.08);
}
.sls-toast-ok { background: #eaf7ef; color: #226243; font-weight: 600; }
.sls-toast-err { background: #fdeaea; color: #9b2c2c; font-weight: 600; }
.sls-slide-up-enter-active, .sls-slide-up-leave-active { transition: opacity .22s ease, transform .22s ease; }
.sls-slide-up-enter, .sls-slide-up-leave-to { opacity: 0; transform: translateY(10px); }
</style>



// WEBPACK FOOTER //
// SellersSettings.vue?a0a67404